/**
 * The public-page type roles and spacing rhythm (AGENTS.md §0, plan item S4).
 * The table in ./README.md is the spec; every kit component and every page
 * built on the kit styles text through these constants and nothing else.
 */

/** The page's one H1. */
export const PAGE_TITLE = 'text-display-3 font-bold text-[var(--rt-ink)]';
/** The sentence(s) under the H1. */
export const LEAD = 'text-lead text-[var(--rt-ink-dim)]';
/** The page's direct answer (`AnswerBox`): the lead's size, in ink, because
 *  it is the one sentence a visitor came for, not an introduction to it. */
export const ANSWER = 'text-lead text-[var(--rt-ink)]';
/** Every section heading (H2). */
export const SECTION = 'text-heading font-semibold text-[var(--rt-ink)]';
/** Running copy that states the thing: answers, list items, table cells. */
export const CONTENT = 'text-body text-[var(--rt-ink)]';
/** A name inside a list: an FAQ question, a card or step name. Content, set bold. */
export const CONTENT_NAME = 'text-body font-semibold text-[var(--rt-ink)]';
/** Running copy that explains or elaborates on content. */
export const EXPLANATION = 'text-body text-[var(--rt-ink-dim)]';
/** Breadcrumbs, bylines, captions, a list item's note, references. */
export const SECOND_LINE = 'text-body-sm text-[var(--rt-ink-dim)]';
/** Readout label, above its value. */
export const READOUT_LABEL = 'text-overline uppercase text-[var(--rt-ink-dim)]';
/** Readout value. */
export const READOUT_VALUE = 'text-stat font-semibold tabular-nums text-[var(--rt-ink)]';

/**
 * A readout's status hue, for a value that reports an outcome (AGENTS.md
 * status-readout rule): a verdict, a check that failed. Never on an action,
 * never as emphasis. `!` is load-bearing: without it, this and
 * READOUT_VALUE's own ink colour both set `color`, and the stylesheet's
 * order, not the class string's, decides which wins (the S7 bug that left a
 * failing verdict in plain ink).
 */
export type ReadoutTone = 'ok' | 'warn' | 'danger';
export const READOUT_TONE: Record<ReadoutTone, string> = {
  ok: '!text-[var(--rt-ok)]',
  warn: '!text-[var(--rt-warn)]',
  danger: '!text-[var(--rt-danger)]',
};
/** A verdict inside a row, a card or a grid cell: the second line's size, set
 *  bold, in its tone's hue (ink when it has none). A readout, never a link. */
export const READOUT_TAG = 'text-body-sm font-semibold text-[var(--rt-ink)]';

/** An inline link inside any text role: inherits the role's size, inks the word. */
export const LINK =
  'text-[var(--rt-ink)] underline decoration-[var(--app-border-bright)] underline-offset-4 hover:decoration-[var(--rt-ink)]';

/** A data table: content-size, figures aligned. */
export const TABLE = 'w-full text-left tabular-nums text-body text-[var(--rt-ink)]';
/** Every cell of a data table, header cells included: a hairline under each row. */
export const TABLE_CELL = 'border-b border-[var(--app-border)] py-2 pr-4';

/** Between sections of a page. */
export const PAGE_RHYTHM = 'space-y-12';
/** Inside a section: heading to content, paragraph to paragraph. */
export const SECTION_RHYTHM = 'space-y-4';
/** Between cells of a grid. */
export const GRID_GAP = 'gap-6';

/** The one raised surface on public pages: a calculator, a card, the editor call to action. */
export const PANEL = 'rounded-xl border border-[var(--app-border-bright)] bg-[var(--app-card)] p-6';
